import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  postRegister,
  postActivateAccount,
  postSetPassword,
  postReactivateAccount,
} from '../../api/account';

const INITIAL_REGISTER_STATE = {
  registrationData: {
    email: '',
    id: '',
    name: '',
  },
  registrationActivation: {
    verificationCode: '',
    isVerificationCodeResend: false,
  },
  isRegistered: false,
  registrationPassword: {
    password: '',
    confirmPassword: '',
  },
  isActivated: false,
  xPasswordReset: '',
  isRegistrationCompleted: false,
  loading: 'idle',
  error: null,
  currentRequestId: undefined,
};

export const registerUser = createAsyncThunk(
  'auth/register/registerUser',
  async (registrationData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postRegister(registrationData, {
        cancelToken: source.token,
      });
      return {
        registrationData,
        data: response.data,
      };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const activateAccount = createAsyncThunk(
  'auth/register/activateAccount',
  async (activationData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postActivateAccount(activationData, {
        cancelToken: source.token,
      });
      return {
        activationData,
        headers: response.headers,
      };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const setPassword = createAsyncThunk(
  'auth/register/setPassword',
  async (setPasswordData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postSetPassword(setPasswordData, {
        cancelToken: source.token,
        headers: {
          'x-password-reset': setPasswordData.xPasswordReset,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const resendVerificationCode = createAsyncThunk(
  'auth/register/resendVerificationCode',
  async (resendVerificationCodeData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postReactivateAccount(resendVerificationCodeData, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const RegisterSlice = createSlice({
  name: 'register',
  initialState: INITIAL_REGISTER_STATE,
  reducers: {
    resetRegistration: () => {
      localStorage.removeItem('register');
      return INITIAL_REGISTER_STATE;
    },
  },
  extraReducers: {
    [registerUser.pending]: (state, { meta }) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = meta.requestId;
      }
    },
    [registerUser.fulfilled]: (state, { payload, meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.isRegistered = true;
        state.registrationData = payload.registrationData;
        state.currentRequestId = undefined;
        state.error = null;
        localStorage.setItem(
          'register',
          JSON.stringify({
            ...payload.registrationData,
          }),
        );
      }
    },
    [registerUser.rejected]: (state, { payload, meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.error = payload;
        state.currentRequestId = undefined;
      }
    },
    [activateAccount.pending]: (state, { meta }) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = meta.requestId;
      }
    },
    [activateAccount.fulfilled]: (state, { payload, meta }) => {
      const { headers, activationData } = payload;

      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.isActivated = true;
        state.isRegistered = true;
        state.registrationData.id = activationData.id;
        state.xPasswordReset = headers['x-password-reset'];
        state.currentRequestId = undefined;
        state.error = null;
      }
    },
    [activateAccount.rejected]: (state, { payload, meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.error = payload;
        state.currentRequestId = undefined;
      }
    },
    [setPassword.pending]: (state, { meta }) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = meta.requestId;
      }
    },
    [setPassword.fulfilled]: (state, { meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.isRegistrationCompleted = true;
        state.isActivated = false;
        state.xPasswordReset = '';
        state.currentRequestId = undefined;
        state.error = null;
      }
    },
    [setPassword.rejected]: (state, { payload, meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.error = payload;
        state.currentRequestId = undefined;
      }
    },
    [resendVerificationCode.pending]: (state, { meta }) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = meta.requestId;
      }
    },
    [resendVerificationCode.fulfilled]: (state, { meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.registrationActivation.isVerificationCodeResend = true;

        state.loading = 'idle';
        state.currentRequestId = undefined;
        state.error = null;
      }
    },
    [resendVerificationCode.rejected]: (state, { payload, meta }) => {
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = 'idle';
        state.error = payload;
        state.currentRequestId = undefined;
      }
    },
  },
});

export const { resetRegistration } = RegisterSlice.actions;

export default RegisterSlice.reducer;

export const selectorIsActivatedRegister = state =>
  state.auth.register.isActivated;
export const selectorIsRegisteredRegister = state =>
  state.auth.register.isRegistered;
export const selectorErrorRegister = state => state.auth.register.error;
export const selectorRegistrationData = state =>
  state.auth.register.registrationData;
export const selectorRegistrationActivation = state =>
  state.auth.register.registrationActivation;
export const selectorXPasswordReset = state =>
  state.auth.register.xPasswordReset;
export const selectorIsRegistrationCompleted = state =>
  state.auth.register.isRegistrationCompleted;
